import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { apiDelete, apiGet, apiPost } from '@/api/client';
import type { CompanyMember, DepartmentMember, PlatformUser } from '@/api/types';
import { AsyncSection } from '@/app/components/AsyncSection';
import { SubmitError } from '@/app/components/SubmitError';
import { Button } from '@/app/components/ui/Button';
import { Card } from '@/app/components/ui/Card';
import { DataTable } from '@/app/components/ui/DataTable';
import { EmptyState } from '@/app/components/ui/EmptyState';
import { Field, FormActions, Input, Select } from '@/app/components/ui/Field';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { useAsync } from '@/lib/useAsync';
import { useSubmit } from '@/lib/useSubmit';

/**
 * Role assignment, admin-only throughout. The API has no endpoint listing who
 * currently holds company_admin or department_admin, so these pages honestly
 * offer grant and revoke without pretending to show current holders.
 */

function RoleForm({
  label,
  people,
  onGrant,
  onRevoke,
  backTo,
}: {
  label: string;
  people: { user_id: string; name: string }[];
  onGrant: (userId: string) => Promise<void>;
  onRevoke: (userId: string) => Promise<void>;
  backTo: string;
}) {
  const { t } = useTranslation();
  const [userId, setUserId] = useState('');
  const grant = useSubmit(onGrant);
  const revoke = useSubmit(onRevoke);

  return (
    <Card className="max-w-2xl">
      <form className="flex flex-col gap-4" onSubmit={(event) => event.preventDefault()}>
        <Field label={t('roles.form.person')}>
          <Select value={userId} onChange={(e) => setUserId(e.target.value)} required>
            <option value="">{t('roles.form.choose')}</option>
            {people.map((person) => (
              <option key={person.user_id} value={person.user_id}>
                {person.name}
              </option>
            ))}
          </Select>
        </Field>
        <SubmitError error={grant.error ?? revoke.error} />
        <FormActions>
          <Button as={Link} to={backTo} variant="ghost">
            {t('roles.form.back')}
          </Button>
          <Button
            type="button"
            variant="danger"
            disabled={revoke.pending || userId === ''}
            onClick={() => void revoke.submit(userId)}
          >
            {t('roles.form.revoke', { label })}
          </Button>
          <Button
            type="button"
            disabled={grant.pending || userId === ''}
            onClick={() => void grant.submit(userId)}
          >
            {t('roles.form.grant', { label })}
          </Button>
        </FormActions>
      </form>
    </Card>
  );
}

export function CompanyRolesPage() {
  const { t } = useTranslation();
  const { companyId = '' } = useParams();
  const navigate = useNavigate();
  const members = useAsync(
    () =>
      apiGet('/companies/{companyId}/users', {
        params: { companyId },
      }) as unknown as Promise<CompanyMember[]>,
    [companyId],
  );
  const backTo = `/manage/companies/${companyId}`;

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('roles.company.title')} lead={t('roles.company.lead')} />
      <AsyncSection state={members} label={t('roles.company.rowsLabel')}>
        {(rows) => (
          <RoleForm
            label={t('roles.company.label')}
            people={rows}
            backTo={backTo}
            onGrant={async (userId) => {
              await apiPost('/companies/{companyId}/roles', {
                params: { companyId },
                body: { user_id: userId },
              });
              await navigate(backTo);
            }}
            onRevoke={async (userId) => {
              await apiDelete('/companies/{companyId}/roles', {
                params: { companyId },
                body: { user_id: userId },
              });
              await navigate(backTo);
            }}
          />
        )}
      </AsyncSection>
    </section>
  );
}

export function DepartmentRolesPage() {
  const { t } = useTranslation();
  const { departmentId = '' } = useParams();
  const navigate = useNavigate();
  const members = useAsync(
    () =>
      apiGet('/departments/{departmentId}/users', {
        params: { departmentId },
      }) as unknown as Promise<DepartmentMember[]>,
    [departmentId],
  );
  const backTo = `/departments/${departmentId}`;

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('roles.department.title')} lead={t('roles.department.lead')} />
      <AsyncSection state={members} label={t('roles.department.rowsLabel')}>
        {(rows) => (
          <RoleForm
            label={t('roles.department.label')}
            people={rows}
            backTo={backTo}
            onGrant={async (userId) => {
              await apiPost('/departments/{departmentId}/roles', {
                params: { departmentId },
                body: { user_id: userId },
              });
              await navigate(backTo);
            }}
            onRevoke={async (userId) => {
              await apiDelete('/departments/{departmentId}/roles', {
                params: { departmentId },
                body: { user_id: userId },
              });
              await navigate(backTo);
            }}
          />
        )}
      </AsyncSection>
    </section>
  );
}

interface AdminRow {
  user_id: string;
  name: string;
  email: string;
}

/** Platform administrators: list, appoint, retire — the one role list that exists. */
export function PlatformAdminsPage() {
  const { t } = useTranslation();
  const admins = useAsync(() => apiGet('/users/admins') as unknown as Promise<AdminRow[]>, []);
  const platformUsers = useAsync(
    () => apiGet('/users/all') as unknown as Promise<PlatformUser[]>,
    [],
  );
  const [userId, setUserId] = useState('');
  const appoint = useSubmit(async () => {
    await apiPost('/users/{userId}/admin', { params: { userId } });
    setUserId('');
    admins.reload();
  });
  const retire = useSubmit(async (id: string) => {
    await apiDelete('/users/{userId}/admin', { params: { userId: id } });
    admins.reload();
  });

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('roles.admins.title')} lead={t('roles.admins.lead')} />

      <AsyncSection state={admins} label={t('roles.admins.rowsLabel')} skeleton="table">
        {(rows) => (
          <DataTable
            rowKey={(admin) => admin.user_id}
            rows={rows}
            columns={[
              {
                key: 'name',
                header: t('roles.admins.name'),
                render: (admin) => <Link to={`/users/${admin.user_id}`}>{admin.name}</Link>,
              },
              { key: 'email', header: t('roles.admins.email'), render: (admin) => admin.email },
              {
                key: 'actions',
                header: '',
                align: 'end',
                render: (admin) => (
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    disabled={retire.pending}
                    onClick={() => void retire.submit(admin.user_id)}
                  >
                    {t('roles.admins.retire')}
                  </Button>
                ),
              },
            ]}
          />
        )}
      </AsyncSection>
      <SubmitError error={retire.error} />

      <AsyncSection state={platformUsers} label={t('roles.admins.usersLabel')}>
        {(users) => (
          <AppointForm
            users={users}
            currentAdminIds={(admins.data ?? []).map((a) => a.user_id)}
            userId={userId}
            setUserId={setUserId}
            appoint={appoint}
          />
        )}
      </AsyncSection>
    </section>
  );
}

/**
 * Appointing used to mean typing a raw user_id UUID, found some other way
 * first. This searches `/users/all` by name or e-mail instead, narrowed to
 * who a POST /admin can actually target: has a login, and is not an alias of
 * another identity (the target must be the identity that authenticates).
 */
function AppointForm({
  users,
  currentAdminIds,
  userId,
  setUserId,
  appoint,
}: {
  users: PlatformUser[];
  currentAdminIds: string[];
  userId: string;
  setUserId: (id: string) => void;
  appoint: { pending: boolean; error: Error | null; submit: () => Promise<boolean> };
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const eligible = useMemo(
    () =>
      users.filter(
        (u) => u.has_login && u.primary_user_id === null && !currentAdminIds.includes(u.user_id),
      ),
    [users, currentAdminIds],
  );

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle === '') return eligible;
    return eligible.filter(
      (u) => u.name?.toLowerCase().includes(needle) || u.email?.toLowerCase().includes(needle),
    );
  }, [eligible, query]);

  return (
    <Card className="max-w-2xl">
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void appoint.submit();
        }}
      >
        <Field label={t('roles.admins.searchPeople')}>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('roles.admins.searchPlaceholder')}
          />
        </Field>
        <Field label={t('roles.admins.person')}>
          <Select value={userId} onChange={(e) => setUserId(e.target.value)} required>
            <option value="">{t('roles.admins.choose')}</option>
            {matches.map((u) => (
              <option key={u.user_id} value={u.user_id}>
                {u.name ?? u.email ?? u.user_id}
                {u.email && u.name ? ` (${u.email})` : ''}
              </option>
            ))}
          </Select>
        </Field>
        {matches.length === 0 && <EmptyState text={t('roles.admins.noEligible')} />}
        <SubmitError error={appoint.error} />
        <FormActions>
          <Button type="submit" disabled={appoint.pending || userId.trim() === ''}>
            {t('roles.admins.appoint')}
          </Button>
        </FormActions>
      </form>
    </Card>
  );
}
